/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { convertChannelCasing } from 'lib-shared-api-utils';
import type {
  Conversation,
  MainConversation,
  RequesterConversation,
} from 'lib-shared-types-api';

/**
 * Transforms a raw conversation.
 *
 * @param conversation The raw conversation from a response.
 */
export function transformConversation(conversation: any): Conversation {
  transformConversationInitialChannel(conversation);
  transformConversationAttributes(conversation.attributes);

  return conversation;
}

/**
 * Transforms a raw main conversation.
 *
 * @param mainConversation The raw main conversation from a response.
 */
export function transformMainConversation(
  mainConversation: any,
): MainConversation {
  transformConversationInitialChannel(mainConversation);

  const eventsWithChannel: Extract<
    MainConversation['events'][number]['type'],
    | 'conversation:conversational_assistant_message'
    | 'conversation:message_added'
  >[] = [
    'conversation:conversational_assistant_message',
    'conversation:message_added',
  ];
  mainConversation.events
    .filter((event) => eventsWithChannel.includes(event.type))
    .forEach((event) => {
      event.channel = convertChannelCasing(event.channel, 'snake');
    });

  mainConversation.events
    .filter((event) => event.type === 'conversation:message_added')
    .forEach(transformConversationStringifiedProperties);

  return mainConversation;
}

/**
 * Transforms a raw requester conversation.
 *
 * @param requesterConversation The raw requester conversation from a response.
 */
export function transformRequesterConversation(
  requesterConversation: any,
): RequesterConversation {
  transformConversationInitialChannel(requesterConversation);
  transformConversationAttributes(requesterConversation.attributes);

  return requesterConversation;
}

/**
 * Transforms the conversation initial channel.
 *
 * @param conversation The raw conversation from a response.
 */
export function transformConversationInitialChannel(conversation: any): void {
  conversation.initial_channel = convertChannelCasing(
    conversation.channel_reference || conversation.initial_channel,
    'snake',
  );
}

/**
 * Transforms wrongly stringified properties found across various conversation
 * entities.
 *
 * @param entity The object on which the properties reside.
 */
export function transformConversationStringifiedProperties(entity: any): void {
  if (typeof entity.direction === 'string') {
    entity.direction = entity.direction.toLowerCase();
  }

  if (typeof entity.duration === 'string') {
    entity.duration = parseInt(entity.duration);
  }

  if (typeof entity.content_type == 'string') {
    entity.content_type = entity.content_type.toLowerCase();
  }

  if (typeof entity.is_story == 'string') {
    entity.is_story = JSON.parse(entity.is_story);
  }

  if (typeof entity.bcc_list_v2 === 'string') {
    entity.bcc_list = JSON.parse(entity.bcc_list_v2);
    delete entity.bcc_list_v2;
  }

  if (typeof entity.cc_list_v2 === 'string') {
    entity.cc_list = JSON.parse(entity.cc_list_v2);
    delete entity.cc_list_v2;
  }

  if (typeof entity.to_list_v2 === 'string') {
    entity.to_list = JSON.parse(entity.to_list_v2);
    delete entity.to_list_v2;
  }

  if (typeof entity.from_contact_point === 'string') {
    entity.from_contact_point = JSON.parse(entity.from_contact_point);
  }

  if (typeof entity.attached_files_v2 === 'string') {
    const attachedFiles = JSON.parse(entity.attached_files_v2);
    entity.attached_files = Array.isArray(attachedFiles)
      ? attachedFiles
      : [attachedFiles];
    delete entity.attached_files_v2;
  }

  if (typeof entity.inline_images_v2 === 'string') {
    const inlineImages = JSON.parse(entity.inline_images_v2);
    entity.inline_images = Array.isArray(inlineImages)
      ? inlineImages
      : [inlineImages];
    delete entity.inline_images_v2;
  }

  if (typeof entity.reply_default_bcc_emails === 'string') {
    entity.reply_default_bcc_emails = JSON.parse(
      entity.reply_default_bcc_emails,
    );
  }

  if (typeof entity.reply_default_cc_emails === 'string') {
    entity.reply_default_cc_emails = JSON.parse(entity.reply_default_cc_emails);
  }

  if (typeof entity.reply_default_to_emails === 'string') {
    entity.reply_default_to_emails = JSON.parse(entity.reply_default_to_emails);
  }
}

/**
 * Transforms the attributes of a conversation.
 *
 * @param attributes The raw attributes from a response.
 */
export function transformConversationAttributes(attributes: any): void {
  if (typeof attributes.inbound_message_count === 'string') {
    attributes.inbound_message_count = JSON.parse(
      attributes.inbound_message_count,
    );
  }

  if (typeof attributes.outbound_message_count === 'string') {
    attributes.outbound_message_count = JSON.parse(
      attributes.outbound_message_count,
    );
  }

  if (typeof attributes.csat_eligible === 'string') {
    attributes.csat_eligible = JSON.parse(attributes.csat_eligible);
  }

  if (typeof attributes.record_conversation === 'string') {
    attributes.record_conversation = JSON.parse(attributes.record_conversation);
  }

  if (typeof attributes.outbound_enabled === 'string') {
    attributes.outbound_enabled = JSON.parse(attributes.outbound_enabled);
  }
}
